import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { IIndicatorMap } from "@gtmhub/error-handling";
import { INotificationSettings, IPatchedNotificationSettings } from "@gtmhub/notifications/models/notifications";
import { NotificationsService } from "@gtmhub/notifications/services/notificationsService";
import { ISettingParameters, NotificationIndicators, NotificationSettingType } from "@webapp/notification/models/notification-settings.model";
import { ToggleChangeService } from "@webapp/notification/services/toggle-change.service.ts.service";
import { IPromise } from "angular";
import { Subscription } from "rxjs";

@Component({
  selector: "gh-account-notification",
  templateUrl: "./account-notification.component.html",
  styleUrls: ["./account-notification.component.less"],
})
export class AccountNotificationComponent implements OnInit, OnDestroy {
  @Input()
  public notificationSettings: INotificationSettings;
  @Input()
  public cancel: () => void;

  public indicators: IIndicatorMap = {
    patchIndicator: { progress: false },
    loadingIndicator: { progress: true },
  };

  private toggleChangeSubscription$: Subscription;

  private static patchedNotificationsSettingsMap: Map<NotificationSettingType, Map<string, IPatchedNotificationSettings>> = new Map<
    NotificationSettingType,
    Map<string, IPatchedNotificationSettings>
  >();

  constructor(private notificationsService: NotificationsService, private toggleChangeService: ToggleChangeService) {}

  public ngOnInit(): void {
    this.toggleChangeSubscription$ = this.toggleChangeService.getToggleStateChange$().subscribe((parameters) => {
      this.onChangeAccountToggles(parameters);
    });
    AccountNotificationComponent.patchedNotificationsSettingsMap.set("inboxNotificationSettings", new Map<string, IPatchedNotificationSettings>());
    AccountNotificationComponent.patchedNotificationsSettingsMap.set("emailNotificationSettings", new Map<string, IPatchedNotificationSettings>());
    this.loadAccountSettings();
  }

  public ngOnDestroy(): void {
    if (this.toggleChangeSubscription$) {
      this.toggleChangeSubscription$.unsubscribe();
    }
  }

  public saveAccountNotification(): IPromise<void> {
    if (
      !AccountNotificationComponent.patchedNotificationsSettingsMap.get("inboxNotificationSettings").size &&
      !AccountNotificationComponent.patchedNotificationsSettingsMap.get("emailNotificationSettings").size
    ) {
      this.cancel();
      return;
    }

    const patchedNotificationSettings: IPatchedNotificationSettings = {
      emailNotificationSettings: {
        embedded: {},
      },
      inboxNotificationSettings: {
        embedded: {},
      },
    };
    const inboxNotificationSettingsMap = AccountNotificationComponent.patchedNotificationsSettingsMap.get("inboxNotificationSettings");
    for (const key of inboxNotificationSettingsMap.keys()) {
      patchedNotificationSettings.inboxNotificationSettings.embedded[key] = inboxNotificationSettingsMap.get(key).inboxNotificationSettings.embedded[key];
    }

    const emailNotificationSettingsMap = AccountNotificationComponent.patchedNotificationsSettingsMap.get("emailNotificationSettings");
    for (const key of emailNotificationSettingsMap.keys()) {
      patchedNotificationSettings.emailNotificationSettings.embedded[key] = emailNotificationSettingsMap.get(key).emailNotificationSettings.embedded[key];
    }

    return this.notificationsService
      .patchAccountNotificationSettings(patchedNotificationSettings)
      .then(() => {
        AccountNotificationComponent.patchedNotificationsSettingsMap = new Map<NotificationSettingType, Map<string, IPatchedNotificationSettings>>();
        this.cancel();
      })
      .catch((error) => {
        this.indicators.patchIndicator = { error };
      });
  }

  public resetIndicator(key: NotificationIndicators): void {
    delete this.indicators[key];
  }

  private onChangeAccountToggles(settingParameters: ISettingParameters): void {
    const patchedSettingsByType = AccountNotificationComponent.patchedNotificationsSettingsMap.get(settingParameters.notificationType);
    patchedSettingsByType.set(settingParameters.settingKey, settingParameters.patchedSetting);
  }

  private loadAccountSettings(): void {
    this.notificationsService
      .getAccountNotificationSettings()
      .then((notificationSettings) => {
        this.notificationSettings = notificationSettings;
        this.resetIndicator("loadingIndicator");
      })
      .catch((error) => {
        this.indicators.loadingIndicator = { error };
      });
  }
}
